import { useMemo } from 'react';
import {
  ArrowRight,
  BarChart3,
  Briefcase,
  CheckCircle2,
  Clock,
  FileText,
  MessageSquare,
  Plus,
  ShieldCheck,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatCurrency } from '../../data/ownerPrototype';
import { useOwnerManpro } from '../../context/OwnerManproContext';
import type { OwnerProjectDto, OwnerProjectStatus } from '../../../api/manpro';



type DashboardStat = {
  label: string;
  value: number;
  caption: string;
  icon: LucideIcon;
  tone: string;
};

type QuickAction = {
  to: string;
  label: string;
  icon: LucideIcon;
};

const statusLabels: Record<OwnerProjectStatus, string> = {
  draft: 'Draft',
  open_for_bid: 'Aktif',
  proposal_review: 'Review Penawaran',
  accepted: 'Provider Diterima',
  converted_to_manpro: 'Monitoring',
  cancelled: 'Dihentikan',
  completed: 'Selesai',
};

const getOwnerStatusLabel = (status: OwnerProjectStatus) => statusLabels[status] ?? status;

const getProjectBudget = (project: OwnerProjectDto) => project.budget_max ?? project.budget_min ?? 0;

const getProjectBudgetLabel = (project: OwnerProjectDto) => {
  if (project.budget_range) return project.budget_range;
  const budget = getProjectBudget(project);
  return budget > 0 ? formatCurrency(budget) : 'Budget belum diisi';
};

// Dashboard Owner hanya membutuhkan estimasi progress ringkas dari status API.
const getProjectProgress = (project: OwnerProjectDto) => {
  if (typeof project.progress_percent === 'number' && project.progress_percent >= 0) return project.progress_percent;
  if (project.status === 'completed') return 100;
  if (project.status === 'converted_to_manpro') return 20;
  if (project.status === 'accepted') return 15;
  if (project.status === 'proposal_review') return 10;
  if (project.status === 'open_for_bid') return 5;
  return 0;
};

const Beranda = () => {
  const { projectSummary, projects, isLoadingProjects } = useOwnerManpro();

  const activeProjects = projects.filter((project) => project.status !== 'completed');
  const completedProjects = projects.filter((project) => project.status === 'completed');
  const totalBid = projectSummary.totalIncomingBids;
  const totalBudget = projects.reduce((total, project) => total + getProjectBudget(project), 0);

  const activityItems = useMemo(
    () =>
      projects.slice(0, 4).map((project) => ({
        id: project.id,
        title: project.title,
        subtitle: `${getOwnerStatusLabel(project.status)} • ${project.location || project.city || '-'}`,
        meta: getProjectBudgetLabel(project),
        progress: getProjectProgress(project),
      })),
    [projects],
  );

  return (
    <div className="space-y-6">

      <section className="overflow-hidden rounded-3xl border border-blue-100 bg-gradient-to-br from-blue-700 via-blue-600 to-sky-500 p-6 text-white shadow-xl shadow-blue-100">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-extrabold ring-1 ring-white/20">
              <ShieldCheck size={15} />
              Project Owner Marketplace
            </span>
            <h1 className="mt-5 max-w-3xl text-4xl font-extrabold tracking-tight">
              Kelola tender, penawaran vendor, dan komunikasi proyek dalam satu portal.
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-6 text-blue-50">
              Dashboard ini menghubungkan flow owner: pasang proyek, review penawaran, shortlist vendor, sampai proyek siap dikonversi ke MANPRO internal.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/owner/pasang"
                className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-extrabold text-blue-700 shadow-sm transition hover:bg-blue-50"
              >
                <Plus size={17} />
                Pasang Proyek Baru
              </Link>
              <Link
                to="/owner/penawaran-masuk"
                className="inline-flex items-center gap-2 rounded-xl bg-blue-950/20 px-5 py-3 text-sm font-extrabold text-white ring-1 ring-white/25 transition hover:bg-blue-950/30"
              >
                Review Penawaran
                <ArrowRight size={17} />
              </Link>
            </div>
          </div>

          <div className="rounded-3xl bg-white/12 p-5 ring-1 ring-white/20 backdrop-blur">
            <p className="text-sm font-extrabold text-blue-50">Nilai proyek aktif</p>
            <p className="mt-3 text-3xl font-extrabold">{formatCurrency(totalBudget || 0)}</p>
            <div className="mt-5 grid grid-cols-3 gap-3">
              {[
                [activeProjects.length, 'Aktif'],
                [totalBid, 'Bid'],
                [completedProjects.length, 'Selesai'],
              ].map(([value, label]) => (
                <div key={label} className="rounded-2xl bg-white/15 p-3 text-center">
                  <p className="text-2xl font-extrabold">{value}</p>
                  <p className="mt-1 text-xs font-semibold text-blue-50">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {([
          { label: 'Proyek Aktif', value: activeProjects.length, caption: 'Sedang berjalan', icon: Briefcase, tone: 'bg-blue-50 text-blue-700' },
          { label: 'Penawaran Masuk', value: totalBid, caption: 'Proposal vendor', icon: FileText, tone: 'bg-emerald-50 text-emerald-700' },
          { label: 'Butuh Review', value: projectSummary.totalSubmittedBids, caption: 'Bid belum diputuskan', icon: MessageSquare, tone: 'bg-violet-50 text-violet-700' },
          { label: 'Vendor Tersimpan', value: 24, caption: 'Shortlist B2B', icon: Users, tone: 'bg-amber-50 text-amber-700' },
        ] satisfies DashboardStat[]).map((item) => {
          const Icon = item.icon;

          return (
          <div key={item.label} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-extrabold text-slate-700">{item.label}</p>
                <p className="mt-3 text-3xl font-extrabold text-slate-900">{item.value}</p>
                <p className="mt-2 text-xs font-semibold text-slate-500">{item.caption}</p>
              </div>
              <div className={`flex h-12 w-12 items-center justify-center rounded-full ${item.tone}`}>
                <Icon size={23} />
              </div>
            </div>
          </div>
          );
        })}
      </section>

      <section className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <div>
              <h2 className="font-extrabold text-slate-900">Aktivitas Proyek Terbaru</h2>
              <p className="mt-1 text-xs text-slate-500">Progress dari proyek yang Anda pasang.</p>
            </div>
            <Link to="/owner/proyek-saya" className="inline-flex items-center gap-1 text-sm font-extrabold text-blue-700">
              Lihat semua <ArrowRight size={15} />
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            {activityItems.map((item) => (
              <div key={item.id} className="grid gap-4 px-5 py-4 lg:grid-cols-[minmax(0,1fr)_180px_160px] lg:items-center">
                <div className="min-w-0">
                  <p className="truncate text-sm font-extrabold text-slate-900">{item.title}</p>
                  <p className="mt-1 text-xs font-medium text-slate-500">{item.subtitle}</p>
                </div>
                <div>
                  <div className="h-2 rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-blue-600" style={{ width: `${item.progress}%` }} />
                  </div>
                  <p className="mt-1 text-xs font-bold text-slate-500">{item.progress}% progress</p>
                </div>
                <p className="text-sm font-extrabold text-slate-800">{item.meta}</p>
              </div>
            ))}
            {activityItems.length === 0 && (
              <div className="px-5 py-10 text-center text-sm text-slate-400">
                {isLoadingProjects ? 'Memuat proyek Owner...' : 'Belum ada proyek.'}
              </div>
            )}
          </div>
        </div>

        <aside className="space-y-4">

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="font-extrabold text-slate-900">Aksi Cepat</h2>
            <div className="mt-4 grid gap-2">
              {([
                { to: '/owner/favorit', label: 'Buka favorit', icon: CheckCircle2 },
                { to: '/owner/penawaran-masuk', label: 'Review komunikasi vendor', icon: Clock },
                { to: '/owner/penawaran-masuk', label: 'Review penawaran', icon: BarChart3 },
              ] satisfies QuickAction[]).map((item) => {
                const Icon = item.icon;

                return (
                <Link key={item.to} to={item.to} className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3 text-sm font-bold text-slate-700 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700">
                  <span className="inline-flex items-center gap-2">
                    <Icon size={17} />
                    {item.label}
                  </span>
                  <ArrowRight size={15} />
                </Link>
                );
              })}
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
};

export default Beranda;
